<template>
  <div class="mb-10">
    <div class="d-flex align-center">
      <v-btn variant="text" size="small" @click="backToList">
        <v-icon start>mdi-chevron-left</v-icon>
        指導計画へ戻る
      </v-btn>
    </div>
    <div class="mt-1">
      <h2>指導計画の帳票設定</h2>
      <div class="text-caption text-grey-darken-1 mt-1">
        帳票レイアウト・転記ルール・承認者・文例を管理します。管理者と施設長のみ利用できます。
      </div>
    </div>

    <v-alert v-if="!canManage" type="warning" variant="tonal" class="mt-4">
      この画面を利用する権限がありません。
    </v-alert>

    <template v-else-if="nurseryId">
      <v-tabs
        :model-value="tab"
        color="primary"
        class="mt-4"
        @update:model-value="onTabChange"
      >
        <v-tab value="template">レイアウト</v-tab>
        <v-tab value="transferRule">転記ルール</v-tab>
        <v-tab value="approver">承認者・提出期限</v-tab>
        <v-tab value="phrase">文例</v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <div class="mt-4">
        <teaching-plan-template-tab
          v-show="tab === 'template'"
          ref="templateTab"
          :nursery-id="nurseryId"
          @templates-updated="templates = $event"
          @open-transfer-rule="openTransferRule"
          @open-phrase="openPhrase"
        ></teaching-plan-template-tab>

        <teaching-plan-transfer-rule-tab
          v-if="tab === 'transferRule'"
          ref="transferRuleTab"
          :key="`transfer-${transferRuleTemplateId || 'default'}`"
          :nursery-id="nurseryId"
          :templates="templates"
          :initial-target-template-id="transferRuleTemplateId"
          @open-template="openTemplateEditor"
        ></teaching-plan-transfer-rule-tab>

        <teaching-plan-approver-tab
          v-if="tab === 'approver'"
          ref="approverTab"
          :nursery-id="nurseryId"
        ></teaching-plan-approver-tab>

        <teaching-plan-phrase-tab
          v-if="tab === 'phrase'"
          :key="`phrase-${phraseTarget ? phraseTarget.templateId : 'default'}`"
          :nursery-id="nurseryId"
          :templates="templates"
          :initial-template-id="phraseTarget ? phraseTarget.templateId : null"
          :initial-phrase-key="phraseTarget ? phraseTarget.phraseKey : null"
        ></teaching-plan-phrase-tab>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { NurseryTeachingPlanTemplateListItem } from "chaild-api/lib";
import TeachingPlanApproverTab from "@/components/teaching-plan/TeachingPlanApproverTab.vue";
import TeachingPlanPhraseTab from "@/components/teaching-plan/TeachingPlanPhraseTab.vue";
import TeachingPlanTemplateTab from "@/components/teaching-plan/TeachingPlanTemplateTab.vue";
import TeachingPlanTransferRuleTab from "@/components/teaching-plan/TeachingPlanTransferRuleTab.vue";
import LocalDataService from "@/service/LocalDataService";
import { confirmDiscard } from "@/utils/TeachingPlanDialog";

/**
 * 指導計画の帳票設定 (NANDO-18 / NANDO-25)。
 * レイアウトタブは編集状態を保つため v-show で残し、他タブは表示時に読み込む。
 */
export default defineComponent({
  name: "TeachingPlanSetting",

  components: {
    TeachingPlanApproverTab,
    TeachingPlanPhraseTab,
    TeachingPlanTemplateTab,
    TeachingPlanTransferRuleTab,
  },

  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },

  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },

  // レイアウトビルダー・転記ルールの未保存分が黙って消えないようにする
  async beforeRouteLeave(to: unknown, from: unknown, next: (go?: false) => void) {
    if (this.hasUnsavedChanges() && !(await confirmDiscard(this.unsavedMessage))) {
      next(false);
      return;
    }
    next();
  },

  data() {
    return {
      tab: "template" as "template" | "transferRule" | "approver" | "phrase",
      unsavedMessage: "保存していない変更があります",
      templates: [] as NurseryTeachingPlanTemplateListItem[],
      transferRuleTemplateId: null as string | null,
      phraseTarget: null as { templateId: string | null; phraseKey: string } | null,
    };
  },

  computed: {
    nurseryId(): number | null {
      return LocalDataService.getNurseryId();
    },
    canManage(): boolean {
      const user = LocalDataService.getUser();
      if (!user || !this.nurseryId) {
        return false;
      }
      const nursery = user.nurserySchools.find((school) => school.nurseryId === this.nurseryId);
      return nursery ? nursery.isAdmin || nursery.isDirector : false;
    },
  },

  methods: {
    /**
     * $refs はリアクティブではないため computed にすると初回評価がキャッシュされる。
     * 呼び出しの都度、子から未保存状態を取り直す。
     */
    isLayoutDirty(): boolean {
      const templateTab = this.$refs.templateTab as
        | InstanceType<typeof TeachingPlanTemplateTab>
        | undefined;
      return templateTab ? templateTab.isLayoutDirty : false;
    },
    /** 転記ルールタブは v-if で破棄されるため、切り替え前に未保存を確認する */
    isTransferRuleDirty(): boolean {
      const transferRuleTab = this.$refs.transferRuleTab as
        | InstanceType<typeof TeachingPlanTransferRuleTab>
        | undefined;
      return transferRuleTab ? transferRuleTab.isDirty : false;
    },
    /** 承認者タブも v-if で破棄されるため、切り替え前に未保存を確認する */
    isApproverDirty(): boolean {
      const approverTab = this.$refs.approverTab as
        | InstanceType<typeof TeachingPlanApproverTab>
        | undefined;
      return approverTab ? approverTab.isDirty : false;
    },
    hasUnsavedChanges(): boolean {
      return this.isLayoutDirty() || this.isTransferRuleDirty() || this.isApproverDirty();
    },
    /**
     * いま開いているタブを離れてよいか。
     *
     * タブ遷移の経路はタブのクリックだけでなく、レイアウト編集からの往復導線
     * (openTransferRule / openPhrase) もある。経路ごとに書くと必ず漏れるので
     * ここに集約し、遷移するところは必ずこれを通す。
     *
     * レイアウトタブは v-show で残るため破棄されないが、将来 v-if に変えても
     * ここを直せば全経路に効くよう、タブ名で分岐する形にしておく。
     */
    async canLeaveCurrentTab(): Promise<boolean> {
      if (this.tab === "transferRule" && this.isTransferRuleDirty()) {
        return confirmDiscard("保存していない転記ルールの変更があります");
      }
      if (this.tab === "approver" && this.isApproverDirty()) {
        return confirmDiscard("保存していない承認者・提出期限の変更があります");
      }
      return true;
    },
    async onTabChange(next: unknown) {
      const nextTab = next as "template" | "transferRule" | "approver" | "phrase";
      if (nextTab === this.tab) {
        return;
      }
      if (!(await this.canLeaveCurrentTab())) {
        return;
      }
      this.tab = nextTab;
    },
    onBeforeUnload(event: BeforeUnloadEvent) {
      if (!this.hasUnsavedChanges()) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    },
    backToList() {
      this.$router.push({ path: "/teaching-plan/list" });
    },
    /** レイアウト編集画面から文例タブへ渡る導線 */
    async openPhrase(payload: { templateId: string | null; phraseKey: string }) {
      if (!(await this.canLeaveCurrentTab())) {
        return;
      }
      this.phraseTarget = payload;
      this.tab = "phrase";
    },
    /** レイアウト編集画面から転記ルールタブへ渡る導線 */
    async openTransferRule(templateId: string) {
      if (!(await this.canLeaveCurrentTab())) {
        return;
      }
      this.transferRuleTemplateId = templateId;
      this.tab = "transferRule";
    },
    /** 転記ルールタブからレイアウト編集へ戻る導線 (往復させる) */
    async openTemplateEditor(templateId: string) {
      if (!(await this.canLeaveCurrentTab())) {
        return;
      }
      this.tab = "template";
      await this.$nextTick();
      const templateTab = this.$refs.templateTab as
        | InstanceType<typeof TeachingPlanTemplateTab>
        | undefined;
      if (templateTab) {
        await templateTab.editById(templateId);
      }
    },
  },
});
</script>
